import { useMemo, useState } from 'react';
import { Loader2, Palette, Search, AlertCircle, History } from 'lucide-react';
import {
  useOrganizationsWithPreset,
  useThemePresetAudit,
  useThemeStats,
  useUpdateOrgThemePreset,
  type OrganizationWithPreset,
} from '@/hooks/useThemes';
import {
  THEME_PRESETS,
  THEME_PRESET_LABELS,
  THEME_PRESET_DESCRIPTIONS,
  type ThemePreset,
} from '@/lib/theme';
import { formatDate, cn } from '@/lib/utils';

type Filter = 'all' | ThemePreset;

function PresetBadge({ preset }: { preset: ThemePreset }) {
  const styles: Record<ThemePreset, string> = {
    blaze: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    lightspeed: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        styles[preset]
      )}
    >
      {THEME_PRESET_LABELS[preset]}
    </span>
  );
}

function StatCard({
  label,
  value,
  description,
}: {
  label: string;
  value: number | string;
  description?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
        {value}
      </p>
      {description ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
  );
}

export default function Themes() {
  const orgsQuery = useOrganizationsWithPreset();
  const auditQuery = useThemePresetAudit(20);
  const updatePreset = useUpdateOrgThemePreset();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const stats = useThemeStats(orgsQuery.data);

  const filteredOrgs = useMemo(() => {
    const orgs = orgsQuery.data ?? [];
    const term = search.trim().toLowerCase();
    return orgs.filter((org) => {
      if (filter !== 'all' && org.preset !== filter) return false;
      if (!term) return true;
      return (
        org.name.toLowerCase().includes(term) ||
        org.slug.toLowerCase().includes(term)
      );
    });
  }, [orgsQuery.data, filter, search]);

  const handleChange = async (org: OrganizationWithPreset, next: ThemePreset) => {
    if (org.preset === next) return;
    const confirmed = window.confirm(
      `Switch ${org.name} from ${THEME_PRESET_LABELS[org.preset]} to ${THEME_PRESET_LABELS[next]}?\n\nUsers of this tenant will see the new theme on their next page load.`
    );
    if (!confirmed) return;

    setPendingOrgId(org.id);
    setErrorMessage(null);
    try {
      await updatePreset.mutateAsync({ organizationId: org.id, preset: next });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update theme';
      setErrorMessage(message);
    } finally {
      setPendingOrgId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Palette className="h-6 w-6 text-orange-500" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Tenant Themes
            </h1>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose the visual theme each client tenant sees in apps/web. Changes
            take effect on the next page load.
          </p>
        </div>
      </div>

      {errorMessage ? (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Could not update theme</p>
            <p>{errorMessage}</p>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total tenants"
          value={stats.total}
          description="Organizations with a theme assignment"
        />
        <StatCard
          label={THEME_PRESET_LABELS.blaze}
          value={stats.blaze}
          description={THEME_PRESET_DESCRIPTIONS.blaze}
        />
        <StatCard
          label={THEME_PRESET_LABELS.lightspeed}
          value={stats.lightspeed}
          description={THEME_PRESET_DESCRIPTIONS.lightspeed}
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants by name or slug..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/40"
            />
          </div>
          <div className="flex items-center gap-1 text-sm">
            {(['all', ...THEME_PRESETS] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-colors',
                  filter === f
                    ? 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                {f === 'all' ? 'All' : THEME_PRESET_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          {orgsQuery.isLoading ? (
            <div className="p-12 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading tenants...
            </div>
          ) : orgsQuery.error ? (
            <div className="p-12 text-center text-sm text-red-600 dark:text-red-400">
              Failed to load tenants:{' '}
              {orgsQuery.error instanceof Error ? orgsQuery.error.message : 'Unknown error'}
            </div>
          ) : filteredOrgs.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No tenants match your filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Tenant
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Slug
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Current theme
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Change to
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrgs.map((org) => {
                  const isPending = pendingOrgId === org.id;
                  return (
                    <tr
                      key={org.id}
                      className="border-b border-gray-100 dark:border-gray-700/60 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {org.logo_url ? (
                            <img
                              src={org.logo_url}
                              alt={org.name}
                              className="h-8 w-8 rounded object-cover bg-gray-100 dark:bg-gray-700"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-500">
                              {org.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="font-medium text-gray-900 dark:text-white">
                            {org.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">
                        {org.slug}
                      </td>
                      <td className="px-4 py-3">
                        <PresetBadge preset={org.preset} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={org.preset}
                            disabled={isPending}
                            onChange={(e) =>
                              handleChange(org, e.target.value as ThemePreset)
                            }
                            className="text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500/40 disabled:opacity-50"
                          >
                            {THEME_PRESETS.map((p) => (
                              <option key={p} value={p}>
                                {THEME_PRESET_LABELS[p]}
                              </option>
                            ))}
                          </select>
                          {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <History className="h-4 w-4 text-gray-400" />
          <h2 className="font-medium text-gray-900 dark:text-white">
            Recent theme changes
          </h2>
        </div>
        <div className="overflow-x-auto">
          {auditQuery.isLoading ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : !auditQuery.data || auditQuery.data.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No theme changes recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    When
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Tenant
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Change
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Changed by
                  </th>
                </tr>
              </thead>
              <tbody>
                {auditQuery.data.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-gray-100 dark:border-gray-700/60 last:border-b-0"
                  >
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                      {entry.organization_name || entry.organization_id}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {entry.from_preset ?? 'unset'} -&gt; {entry.to_preset}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      {entry.admin_user_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
